import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  RiArrowLeftLine, 
  RiFileTextLine, 
  RiUserStarLine,
  RiCalendarLine,
  RiBookOpenLine,
  RiDownloadLine,
  RiEyeLine,
  RiStarFill,
  RiFileCopyLine,
  RiLinksLine,
  RiSearchLine,
  RiFilterLine,
  RiCloseLine,
  RiMailLine,
  RiGlobalLine,
  RiDoubleQuotesL,
  RiDoubleQuotesR,
  RiInformationLine,
  RiArrowUpLine,
  RiMicroscopeLine,
  RiAddLine,
  RiRefreshLine
} from 'react-icons/ri';
import axios from 'axios';
import { API_BASE } from '../config';
import toast from 'react-hot-toast';
import { useSummit } from "../pages/summitdetails";

/* ─── THEME COLORS ───────────────────────────── */
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F9F8",
  softTeal: "#E8F5F3",
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  primaryLight: "#3BBCAE",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
  accent: "#3BBCAE",
  accentGold: "#D4A017",
  accentGoldDark: "#B8880F",
  accentGoldLight: "#E8B520",
  accentTealLight: "#6BC2B5",
  accentTealDark: "#218577",
  text: "#1C3461",
  textMid: "#2D4A5A",
  textMuted: "#6B8090",
  textLight: "#9BB0C0",
  border: "#C8E8E4",
  borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)",
  shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  shadowGold: "0 8px 32px rgba(212,160,23,0.25)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
  gradGoldReverse: "linear-gradient(135deg, #E8B520 0%, #D4A017 100%)",
  gradTealDark: "linear-gradient(135deg, #1F7A6E 0%, #2A9D8F 100%)",
  gradPurple: "linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)",
  gradGreen: "linear-gradient(135deg, #10B981 0%, #34D399 100%)",
  success: "#10B981",
  warning: "#D4A017",
  danger: "#218577",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth}
  body,#root{background:${T.offWhite};color:${T.text};font-family:'Inter',sans-serif}
  
  ::-webkit-scrollbar{width:8px}
  ::-webkit-scrollbar-track{background:${T.borderLight}}
  ::-webkit-scrollbar-thumb{background:${T.primaryLight};border-radius:4px}

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @media (max-width: 768px) {
    section { padding: 48px 24px !important; }
  }
`;

export default function SummitPublications() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { eventId: paramEventId } = useParams();
  const summitContext = useSummit();
  
  const eventId = summitContext?.eventId || searchParams.get("eventId") || paramEventId;
  
  const [publications, setPublications] = useState([]);
  const [filteredPublications, setFilteredPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState(['all']);
  const [selectedPublication, setSelectedPublication] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showBtt, setShowBtt] = useState(false);
  const [eventTitle, setEventTitle] = useState('Meetings21');

  useEffect(() => {
    if (eventId) {
      fetchPublications();
      fetchEventDetails();
    } else {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    const fn = () => setShowBtt(window.scrollY > 500);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showModal]);

  const fetchEventDetails = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/events/${eventId}`);
      if (response.data && response.data.success) {
        setEventTitle(response.data.event?.title || response.data.event?.name || 'Meetings21');
      }
    } catch (error) {
      console.error('Error fetching event details:', error);
    }
  };

  // ─── ✅ FETCH PUBLICATIONS WITH CACHE‑BUSTER ───
  const fetchPublications = async () => {
    try {
      setLoading(true);
      const timestamp = Date.now();
      const url = `${API_BASE}/api/summit-publications/event/${eventId}?_=${timestamp}`;
      const response = await axios.get(url);
      
      let publicationsData = [];
      if (response.data && response.data.success) {
        publicationsData = response.data.data || [];
      } else if (Array.isArray(response.data)) {
        publicationsData = response.data;
      } else if (response.data && response.data.publications) {
        publicationsData = response.data.publications;
      }
      
      setPublications(publicationsData);
      setFilteredPublications(publicationsData);
      
      if (publicationsData && publicationsData.length > 0) {
        const cats = ['all', ...new Set(publicationsData.map(p => p.category).filter(Boolean))];
        setCategories(cats);
      }
    } catch (error) {
      console.error('Error fetching publications:', error);
      toast.error('Failed to load publications');
      setPublications([]);
      setFilteredPublications([]);
    } finally {
      setLoading(false);
    }
  };

  const seedDefaultPublications = async () => {
    if (!eventId) {
      toast.error('No event ID available');
      return;
    }
    setSeeding(true);
    try {
      const response = await axios.post(
        `${API_BASE}/api/summit-publications/seed/event/${eventId}`,
        {},
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (response.data.success) {
        toast.success(response.data.message || 'Default publications seeded successfully!');
        await fetchPublications();
      } else {
        toast.error(response.data.message || 'Failed to seed publications');
      }
    } catch (error) {
      console.error('Error seeding publications:', error);
      toast.error(error.response?.data?.message || 'Failed to seed default publications');
    } finally {
      setSeeding(false);
    }
  };

  useEffect(() => {
    if (!publications || publications.length === 0) {
      setFilteredPublications([]);
      return;
    }
    
    let filtered = [...publications];
    
    if (searchTerm) {
      filtered = filtered.filter(pub => 
        pub.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pub.authors?.some(author => author.name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        pub.keywords?.some(keyword => keyword?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        pub.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(pub => pub.category === selectedCategory);
    }
    
    setFilteredPublications(filtered);
  }, [searchTerm, selectedCategory, publications]);

  const handleDownload = async (pubId, e) => {
    e.stopPropagation();
    try {
      const response = await axios.post(`${API_BASE}/api/summit-publications/${pubId}/download`);
      if (response.data.success) {
        toast.success('Download recorded!');
      }
    } catch (error) {
      console.error('Error recording download:', error);
    }
    
    const pub = publications.find(p => p._id === pubId);
    if (pub && pub.pdfUrl) {
      window.open(pub.pdfUrl, '_blank');
    }
  };

  const openDetailsModal = (pub) => {
    setSelectedPublication(pub);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPublication(null);
  };

  const getCategoryColor = (category) => {
    const colors = {
      research: T.primary,
      review: "#8B5CF6",
      "case-study": T.success,
      "clinical-trial": T.warning,
      editorial: T.danger,
      venue: T.accentGold,
      other: T.textMuted
    };
    return colors[category] || T.textMuted;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      research: "Research Article",
      review: "Review Article",
      "case-study": "Case Study",
      "clinical-trial": "Clinical Trial",
      editorial: "Editorial",
      venue: "Venue Info",
      other: "Other"
    };
    return labels[category] || category || "Publication";
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: T.offWhite, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{css}</style>
        <div style={{ width: 48, height: 48, border: `3px solid ${T.border}`, borderTop: `3px solid ${T.primary}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      </div>
    );
  }

  return (
    <>
      <style>{css}</style>
      
      <div style={{ minHeight: '100vh', background: T.offWhite }}>

        {/* Hero Section */}
        <section style={{ padding: 'clamp(48px,7vw,80px) clamp(20px,4vw,64px) clamp(40px,5vw,64px)', background: T.gradSoft, borderBottom: `1px solid ${T.border}`, position: 'relative', overflow: 'hidden' }}>
          <div style={{ maxWidth: 1320, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
              <span style={{ 
                padding: '5px 14px', 
                borderRadius: 20, 
                background: T.softTeal, 
                border: `1px solid ${T.border}`, 
                color: T.primary, 
                fontSize: 11, 
                fontWeight: 700, 
                textTransform: 'uppercase', 
                letterSpacing: '0.1em' 
              }}>
                <RiMicroscopeLine size={13} style={{ display: 'inline', marginRight: 6 }} />
                Publications
              </span>
            </div>
            <h1 style={{ 
              fontFamily: "'Playfair Display', serif", 
              fontSize: 'clamp(2rem, 4.5vw, 3.4rem)', 
              fontWeight: 900, 
              color: T.navy, 
              letterSpacing: '-0.03em', 
              lineHeight: 1.06, 
              marginBottom: 14 
            }}>
              {eventTitle} <span style={{ color: T.accentGold, fontStyle: 'normal' }}>Publications</span>
            </h1>
            <p style={{ 
              fontSize: 'clamp(0.88rem, 1.3vw, 1rem)', 
              color: T.textMid, 
              lineHeight: 1.7, 
              maxWidth: 560 
            }}>
              Explore research papers, reviews, and proceedings from our academic events
            </p>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 24 }}>
              <div style={{ flex: 1, height: 2, maxWidth: 80, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
              <div style={{ width: 8, height: 8, background: T.accentGold, transform: 'rotate(45deg)' }} />
              <div style={{ flex: 1, height: 2, maxWidth: 80, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: '24px 40px 0' }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
            <div style={{ background: T.white, borderRadius: 12, padding: '16px 24px', border: `1px solid ${T.border}`, flex: '1 1 auto', minWidth: 120, boxShadow: T.shadow }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: T.accentGold }}>{publications.length}</div>
              <div style={{ fontSize: 12, color: T.textMuted }}>Total Publications</div>
            </div>
            <div style={{ background: T.white, borderRadius: 12, padding: '16px 24px', border: `1px solid ${T.border}`, flex: '1 1 auto', minWidth: 120, boxShadow: T.shadow }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: T.primary }}>{categories.length - 1}</div>
              <div style={{ fontSize: 12, color: T.textMuted }}>Categories</div>
            </div>
            <div style={{ background: T.white, borderRadius: 12, padding: '16px 24px', border: `1px solid ${T.border}`, flex: '1 1 auto', minWidth: 120, boxShadow: T.shadow }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: T.accentTealLight }}>
                {publications.reduce((sum, pub) => sum + (pub.downloads || 0), 0)}
              </div>
              <div style={{ fontSize: 12, color: T.textMuted }}>Total Downloads</div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: '0 40px 80px' }}>
          
          {/* Action Buttons */}
          <div style={{ marginBottom: 32, display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
            <Link 
              to={`/agenda/${eventId}`} 
              style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: 8, 
                color: T.primary, 
                textDecoration: 'none',
                fontWeight: 500,
                padding: '8px 0',
                transition: 'gap 0.3s'
              }}
              onMouseEnter={e => e.currentTarget.style.gap = '12px'}
              onMouseLeave={e => e.currentTarget.style.gap = '8px'}
            >
              <RiArrowLeftLine size={20} /> Back to Summit
            </Link>
            
            {publications.length === 0 && (
              <button
                onClick={seedDefaultPublications}
                disabled={seeding}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 24px',
                  borderRadius: 40,
                  background: T.gradGold,
                  color: T.navyDeep,
                  border: 'none',
                  cursor: seeding ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  opacity: seeding ? 0.7 : 1
                }}
              >
                {seeding ? <div style={{ width: 16, height: 16, border: `2px solid ${T.navyDeep}`, borderTop: `2px solid transparent`, borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} /> : <RiAddLine size={16} />}
                {seeding ? 'Seeding...' : 'Load Sample Publications'}
              </button>
            )}
            
            {publications.length > 0 && (
              <button
                onClick={fetchPublications}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 24px',
                  borderRadius: 40,
                  background: T.white,
                  color: T.primary,
                  border: `1px solid ${T.border}`,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600
                }}
              >
                <RiRefreshLine size={16} /> Refresh
              </button>
            )}
          </div>

          {/* Search and Filter Bar */}
          {publications && publications.length > 0 && (
            <div style={{ 
              background: T.white, 
              borderRadius: 16, 
              padding: 20, 
              marginBottom: 32,
              boxShadow: T.shadow,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 16,
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ 
                flex: 1, 
                minWidth: 250,
                display: 'flex', 
                alignItems: 'center', 
                gap: 12,
                padding: '8px 16px',
                background: T.offWhite,
                borderRadius: 40,
                border: `1px solid ${T.border}`
              }}>
                <RiSearchLine size={18} style={{ color: T.textMuted }} />
                <input
                  type="text"
                  placeholder="Search publications..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    flex: 1,
                    border: 'none',
                    background: 'transparent',
                    outline: 'none',
                    fontSize: 14,
                    fontFamily: "'Inter', sans-serif"
                  }}
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: T.textMuted
                    }}
                  >
                    <RiCloseLine size={16} />
                  </button>
                )}
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <RiFilterLine size={16} style={{ color: T.textMuted }} />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  style={{
                    padding: '8px 32px 8px 16px',
                    borderRadius: 40,
                    border: `1px solid ${T.border}`,
                    background: T.offWhite,
                    fontSize: 14,
                    cursor: 'pointer',
                    outline: 'none',
                    fontFamily: "'Inter', sans-serif"
                  }}
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>
                      {cat === 'all' ? 'All Categories' : getCategoryLabel(cat)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Publications Grid */}
          {!publications || publications.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: 80, 
              background: T.white, 
              borderRadius: 24,
              boxShadow: T.shadow,
              border: `1px solid ${T.border}`
            }}>
              <RiFileTextLine size={64} style={{ color: T.textMuted, marginBottom: 16 }} />
              <h3 style={{ fontSize: 20, fontWeight: 700, color: T.text, marginBottom: 8 }}>No publications found</h3>
              <p style={{ color: T.textMuted, marginBottom: 24 }}>Click "Load Sample Publications" to add demo content</p>
              <button
                onClick={seedDefaultPublications}
                disabled={seeding}
                style={{
                  padding: '12px 28px',
                  borderRadius: 40,
                  background: T.gradGold,
                  color: T.navyDeep,
                  border: 'none',
                  cursor: seeding ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  opacity: seeding ? 0.7 : 1
                }}
              >
                {seeding ? <div style={{ width: 16, height: 16, border: `2px solid ${T.navyDeep}`, borderTop: `2px solid transparent`, borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} /> : <RiAddLine size={18} />}
                {seeding ? 'Loading...' : 'Load Sample Publications'}
              </button>
            </div>
          ) : filteredPublications.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: 60, 
              background: T.white, 
              borderRadius: 24,
              boxShadow: T.shadow,
              border: `1px solid ${T.border}`
            }}>
              <RiSearchLine size={48} style={{ color: T.textMuted, marginBottom: 16 }} />
              <h3 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 8 }}>No matching publications</h3>
              <p style={{ color: T.textMuted }}>Try adjusting your search or filter criteria</p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('all');
                }}
                style={{
                  marginTop: 16,
                  padding: '8px 24px',
                  borderRadius: 40,
                  background: T.gradGold,
                  color: T.navyDeep,
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 600
                }}
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
              {filteredPublications.map((pub, index) => {
                // ─── VENUE CATEGORY ─── (WITHOUT "Venue Info" BADGE)
                if (pub.category === 'venue') {
                  return (
                    <motion.div
                      key={pub._id || index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      style={{
                        background: T.white,
                        borderRadius: 20,
                        padding: '24px 32px',
                        boxShadow: T.shadowMd,
                        border: `2px solid ${T.accentGold}`,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 24,
                      }}
                    >
                      {/* ─── Cover Image ─── */}
                      {pub.coverImage ? (
                        <div style={{ flex: '0 0 80px', minHeight: 80 }}>
                          <img
                            src={pub.coverImage}
                            alt={pub.title || 'Venue'}
                            style={{
                              width: '100%',
                              height: 'auto',
                              maxHeight: 80,
                              objectFit: 'contain',
                              borderRadius: 8,
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      ) : (
                        <div style={{ 
                          flex: '0 0 80px', 
                          minHeight: 80, 
                          background: T.softTeal, 
                          borderRadius: 8, 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center' 
                        }}>
                          <RiInformationLine size={32} style={{ color: T.accentGold }} />
                        </div>
                      )}

                      {/* ─── Content ─── */}
                      <div style={{ flex: 1 }}>
                        {/* ✅ TITLE - NO BADGE */}
                        <h2 style={{
                          fontSize: 'clamp(18px, 2.8vw, 24px)',
                          fontWeight: 700,
                          color: T.text,
                          marginBottom: 8,
                          lineHeight: 1.3,
                        }}>
                          {pub.title || 'Untitled'}
                        </h2>
                        <div
                          dangerouslySetInnerHTML={{ __html: pub.description || pub.abstract || '' }}
                          style={{
                            fontSize: '1.05rem',
                            lineHeight: 1.8,
                            color: T.text,
                          }}
                        />
                        {pub.publicationDate && (
                          <div style={{ marginTop: 12, fontSize: 13, color: T.textMuted }}>
                            <RiCalendarLine style={{ display: 'inline', marginRight: 6 }} />
                            {new Date(pub.publicationDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                }

                // ─── RESEARCH PUBLICATION CARD ───────────────────────────
                return (
                  <motion.div
                    key={pub._id || index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ y: -4, boxShadow: T.shadowMd }}
                    style={{
                      background: T.white,
                      borderRadius: 20,
                      overflow: 'hidden',
                      boxShadow: T.shadow,
                      border: `1px solid ${T.border}`,
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'stretch',
                      minHeight: 220,
                    }}
                    onClick={() => openDetailsModal(pub)}
                  >
                    {/* Cover Image */}
                    <div style={{
                      flex: '0 0 180px',
                      background: pub.coverImage ? `url(${pub.coverImage}) center/cover no-repeat` : T.gradSoft,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 16,
                      borderRight: `1px solid ${T.borderLight}`,
                      minHeight: 200,
                    }}>
                      {pub.coverImage ? (
                        <img
                          src={pub.coverImage}
                          alt={pub.title || 'Publication cover'}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            maxHeight: 180,
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<span style="font-size: 48px;">📄</span>';
                          }}
                        />
                      ) : (
                        <span style={{ fontSize: 56, opacity: 0.5 }}>📄</span>
                      )}
                    </div>

                    {/* Content */}
                    <div style={{
                      flex: 1,
                      padding: '20px 24px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      minWidth: 0,
                    }}>
                      <div>
                        {pub.category && (
                          <span style={{
                            display: 'inline-block',
                            padding: '2px 12px',
                            background: `${getCategoryColor(pub.category)}20`,
                            color: getCategoryColor(pub.category),
                            borderRadius: 20,
                            fontSize: 10,
                            fontWeight: 700,
                            marginBottom: 8,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                          }}>
                            {getCategoryLabel(pub.category)}
                          </span>
                        )}
                        <h2 style={{
                          fontSize: 'clamp(16px, 2.5vw, 20px)',
                          fontWeight: 700,
                          color: T.text,
                          marginBottom: 8,
                          lineHeight: 1.3,
                        }}>
                          {pub.title || 'Untitled'}
                        </h2>
                        
                        {/* Authors */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          marginBottom: 8,
                          flexWrap: 'wrap',
                        }}>
                          <RiUserStarLine size={14} style={{ color: T.textMuted }} />
                          <span style={{
                            fontSize: 13,
                            color: T.textMid,
                          }}>
                            {pub.authors && pub.authors.length > 0
                              ? pub.authors.map(a => a.name).join(', ')
                              : 'Anonymous'}
                          </span>
                        </div>

                        {/* Description */}
                        {pub.description && (
                          <div style={{
                            marginBottom: 12,
                            padding: '8px 12px',
                            background: T.offWhite,
                            borderRadius: 8,
                            border: `1px solid ${T.borderLight}`,
                          }}>
                            <p style={{
                              fontSize: 13,
                              color: T.textMid,
                              lineHeight: 1.6,
                              margin: 0,
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}>
                              {pub.description}
                            </p>
                          </div>
                        )}

                        {/* Meta info */}
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '12px 20px',
                          marginBottom: 8,
                        }}>
                          {pub.journalName && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <RiBookOpenLine size={14} style={{ color: T.textMuted }} />
                              <span style={{ fontSize: 12, color: T.textMuted }}>
                                {pub.journalName}
                                {pub.volume && ` Vol ${pub.volume}`}
                                {pub.issue && `(${pub.issue})`}
                              </span>
                            </div>
                          )}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <RiCalendarLine size={14} style={{ color: T.textMuted }} />
                            <span style={{ fontSize: 12, color: T.textMuted }}>
                              {pub.publicationDate ? new Date(pub.publicationDate).getFullYear() : 'N/A'}
                            </span>
                          </div>
                          {pub.doi && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <RiLinksLine size={14} style={{ color: T.textMuted }} />
                              <span style={{ fontSize: 12, color: T.primary }}>DOI: {pub.doi}</span>
                            </div>
                          )}
                        </div>

                        {/* Keywords */}
                        {pub.keywords && pub.keywords.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                            {pub.keywords.slice(0, 3).map((keyword, idx) => (
                              <span key={idx} style={{
                                padding: '2px 10px',
                                background: T.offWhite,
                                borderRadius: 12,
                                fontSize: 11,
                                color: T.textMuted,
                                border: `1px solid ${T.border}`,
                              }}>
                                {keyword}
                              </span>
                            ))}
                            {pub.keywords.length > 3 && (
                              <span style={{ fontSize: 11, color: T.textMuted }}>
                                +{pub.keywords.length - 3} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Bottom row: stats + buttons */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 12,
                        borderTop: `1px solid ${T.borderLight}`,
                        paddingTop: 14,
                        marginTop: 4,
                      }}>
                        <div style={{ display: 'flex', gap: 16 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: T.textMuted }}>
                            <RiEyeLine size={14} /> {pub.views || 0}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: T.textMuted }}>
                            <RiDownloadLine size={14} /> {pub.downloads || 0}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          {pub.pdfUrl && (
                            <button
                              onClick={(e) => handleDownload(pub._id, e)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                padding: '6px 16px',
                                borderRadius: 30,
                                background: T.gradGold,
                                color: T.navyDeep,
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: 12,
                                fontWeight: 600,
                                transition: 'all 0.2s',
                                boxShadow: T.shadowGold,
                              }}
                              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                              onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                            >
                              <RiDownloadLine size={14} /> PDF
                            </button>
                          )}
                          <button
                            onClick={() => openDetailsModal(pub)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              padding: '6px 16px',
                              borderRadius: 30,
                              background: T.white,
                              color: T.primary,
                              border: `1px solid ${T.primary}`,
                              cursor: 'pointer',
                              fontSize: 12,
                              fontWeight: 600,
                              transition: 'all 0.2s',
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.background = T.primary;
                              e.currentTarget.style.color = T.white;
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.background = T.white;
                              e.currentTarget.style.color = T.primary;
                            }}
                          >
                            Details
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>

        {/* Details Modal (unchanged) */}
        <AnimatePresence>
          {showModal && selectedPublication && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.7)',
                backdropFilter: 'blur(8px)',
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
                overflowY: 'auto'
              }}
              onClick={closeModal}
            >
              <motion.div
                initial={{ scale: 0.9, y: 20, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.9, y: 20, opacity: 0 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                style={{
                  maxWidth: 900,
                  width: '100%',
                  maxHeight: '90vh',
                  overflowY: 'auto',
                  background: T.white,
                  borderRadius: 28,
                  boxShadow: T.shadowLg,
                  position: 'relative'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* ... modal content (same as before) ... */}
                <div
                  style={{
                    position: 'sticky',
                    top: 0,
                    background: T.gradPrimary,
                    padding: '24px 32px',
                    borderRadius: '28px 28px 0 0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    zIndex: 10
                  }}
                >
                  <div>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        background: 'rgba(255,255,255,0.2)',
                        color: T.white,
                        borderRadius: 20,
                        fontSize: 11,
                        fontWeight: 600,
                        marginBottom: 12,
                        textTransform: 'uppercase'
                      }}
                    >
                      {getCategoryLabel(selectedPublication.category).toUpperCase()}
                    </span>
                    <h2 style={{ fontSize: 24, fontWeight: 800, color: T.white, margin: 0, fontFamily: "'Playfair Display', serif" }}>
                      {selectedPublication.title}
                    </h2>
                  </div>
                  <button
                    onClick={closeModal}
                    style={{
                      background: 'rgba(255,255,255,0.2)',
                      border: 'none',
                      width: 40,
                      height: 40,
                      borderRadius: 12,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      color: T.white,
                      transition: 'all 0.2s'
                    }}
                  >
                    <RiCloseLine size={24} />
                  </button>
                </div>
                
                <div style={{ padding: '32px' }}>
                  {/* Authors, description, abstract, journal info, keywords, stats, actions – same as your existing modal */}
                  {/* I'm omitting the long modal code for brevity; it's unchanged. */}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* BACK TO TOP */}
        {showBtt && (
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            style={{
              position: 'fixed',
              bottom: 26,
              right: 26,
              width: 42,
              height: 42,
              borderRadius: 10,
              background: T.gradGold,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: T.navyDeep,
              boxShadow: T.shadowGold,
              zIndex: 100,
            }}
          >
            <RiArrowUpLine size={18} />
          </button>
        )}
      </div>
    </>
  );
}